/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';

/* eslint-enable max-len */

@Component({
	selector: 'adjustment-write-off-comments',
	templateUrl: './adjustment-write-off-comments.component.html'
})

/**
 * A component representing a wizard step to select a note for
 * a term based adjustment write off.
 *
 * @export
 * @class AdjustmentWriteOffCommentsComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AdjustmentWriteOffCommentsComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initalizes a new instance of the
	 * Adjustment Write Off Comments component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes on entity creation.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityInstanceApiService: EntityInstanceApiService)
	{
	}

	/**
	* Gets or sets the context of this dynamic component that will be set
	* during initialization. The source is the content component and
	* the data will be associated data that we desire to pass explicitly.
	*
	* @type {IDynamicComponentContext<
	* 	DynamicWizardComponent,
	* 	IWizardContext>}
	* @memberof AdjustmentWriteOffCommentsComponent
	*/
	@Input() public context:
		IDynamicComponentContext<DynamicWizardComponent, IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[] =
		<FormlyFieldConfig[]>
		[
			{
				key: 'comments',
				type: FormlyConstants.customControls.customTextArea,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					label: 'Comments',
					required: true,
					rows: FormlyConstants.textAreaRowSizes.small,
					gridColumns: FormlyConstants.gridColumns.fullGrid
				}
			}
		];

	/**
	 * Gets the workflow action being executed to update the PolicyTerm
	 * data.
	 *
	 * @type {string}
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public readonly workflowActionName: string =
		'WriteOffCreateLedgerTransaction';

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Initializes the current component.
	 *
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Performs component initialization.
	 *
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public initializeComponent(): void
	{
		this.context.source.addToNext(this.writeOff.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public validityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * Creates the write off and navigates to the Policy Term dashboard.
	 *
	 * @async
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	public async writeOff(): Promise<void>
	{
		let requestBody: any = null;

		let urlParams: any =
			{
				writeOffType: this.currentData.type,
				amount: this.currentData.amount,
				comments: this.currentData.comments
			};

		if (this.currentData.paymentAllocationsEnabled)
		{
			urlParams =
				{
					...urlParams,
					transactionId:
						this.currentData.transactionId
				};

			requestBody =
				{
					allocationReferences:
						this.currentData.paymentAllocations
							.map((allocation: any) => ({
								id: allocation.referenceId,
								allocatedAmount: allocation.allocatedAmount
							}))
				};
		}

		const queryString: string =
			this.entityInstanceApiService
				.formUrlParam(
					AppConstants.empty,
					urlParams);

		await this.writeOffExecution(
			queryString,
			requestBody);

		this.navigateToPolicyTermDashboard(
			this.currentData.id);
	}

	/**
	 * Executes the write off process, which executes the
	 * workflow action with the collected data from the write off wizard.
	 *
	 * @async
	 * @param {string} queryString
	 * The extra parameters sent as query string parameters.
	 * @param {any} requestBody
	 * The request body to be sent in the API call.
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	private async writeOffExecution(
		queryString: string,
		requestBody: any): Promise<void>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups
				.policyTerms;

		await this.activityService.handleActivity(
			new Activity(
				this.entityInstanceApiService
					.executeActionWithPayload(
						this.currentData.id,
						this.workflowActionName,
						requestBody,
						queryString),
				'<strong>Processing write off</strong>',
				'<strong>Write off processed</strong>',
				'A write off has been applied to the policy.',
				'A write off has not been applied to the policy.'));
	}

	/**
	 * This will navigate to the policy term dashboard
	 * using the policy term id provided.
	 *
	 * @param {number} entityId
	 * The policy term entity id to navigate to.
	 * @memberof AdjustmentWriteOffCommentsComponent
	 */
	private navigateToPolicyTermDashboard(
		entityId: number): void
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				AppConstants.viewTypes.edit,
				entityId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}
}